import { useEffect, useRef, useState } from "react";
import { useAuth } from "../../../context/UserContextProvider";
import Mascot from "./Mascot";
import { i18n, useScreenSize } from "../../../library";
import { draggableMascotStyles } from "./DraggableMascotStyles";

interface DraggableMascotProps {
  xAxisOnly?: boolean;
}

const DraggableMascot = (props: DraggableMascotProps) => {
  const { xAxisOnly } = props;
  const {
    userInformation,
    userProfile,
    userProfileSeason,
    userProfileGrade,
    showMascot,
    mascotMode,
    showMascotText,
    setShowMascotText,
    mascotText,
    mascotSubText,
    mascotSubTextList,
    mascotPrimaryButtonText,
    mascotPrimaryButtonOnClick,
    mascotChatActivityId,
    mascotChatActivityTemplateId,
    mascotChatActivityTemplateType,
    mascotChatWelcomeText,
    mascotChatList,
    mascotChatLoading,
    mascotSendMessageOnClick,
    mascotChatCanConvertAudio,
    mascotHasVoiceItem,
    mascotChatCanDraw,
    mascotChatVoiceType,
    mascotChatActType,
    mascotAudioSrc,
    mascotInventoryItemDTOList,
    mascotPosition,
    mascotChatErrorText,
    mascotChatContinueText,
    mascotChatContinueOnClick,
    mascotChatActionList,
  } = useAuth();
  const { screenSize } = useScreenSize();
  const [position, setPosition] = useState({ x: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [canDragable, setCanDragable] = useState(false);
  const [showDragIndicator, setShowDragIndicator] = useState(false);
  const mascotRef = useRef<HTMLDivElement | null>(null);
  const dragHandleRef = useRef<HTMLDivElement | null>(null);
  const hasBeenDraggedRef = useRef(false);
  const [mascotHeight, setMascotHeight] = useState(240);
  const [mascotWidth, setMascotWidth] = useState(240);
  const [mascotChatSize, setMascotChatSize] = useState("xl");
  const dragHeight = mascotHeight * 0.25;
  const positionXOffset = mascotHeight * 2;
  const positionYOffset = mascotHeight * 0.7;

  useEffect(() => {
    const { width, height } = screenSize;
    const minDimension = Math.min(width, height);
    if (minDimension <= 768) {
      setMascotHeight(120);
      setMascotWidth(120);
      setMascotChatSize("md");
    } else if (minDimension <= 1024) {
      setMascotHeight(180);
      setMascotWidth(180);
      setMascotChatSize("lg");
    } else if (minDimension <= 1440) {
      setMascotHeight(210);
      setMascotWidth(210);
      setMascotChatSize("xl");
    } else {
      setMascotHeight(240);
      setMascotWidth(240);
      setMascotChatSize("xl");
    }
  }, [screenSize]);

  useEffect(() => {
    if (!mascotRef.current) return;
    if (hasBeenDraggedRef.current) return;

    const newX = calculateXByState(window.innerWidth);
    setPosition({ x: newX });
  }, [screenSize, positionXOffset, mascotPosition, mascotWidth]);

  useEffect(() => {
    const handleResize = () => {
      if (mascotRef.current) {
        const maxX = window.innerWidth - mascotWidth - 20;
        setPosition((prevPosition) => ({
          x: Math.max(20, Math.min(prevPosition.x, maxX)),
        }));
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [mascotWidth]);

  useEffect(() => {
    if (!isDragging) return;

    const handleMouseMove = (e: MouseEvent) => {
      const newX = e.clientX - dragOffset.x;
      const maxX = window.innerWidth - mascotWidth - 20;
      setPosition({
        x: Math.max(20, Math.min(newX, maxX)),
      });
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    const handleTouchMove = (e: TouchEvent) => {
      const touch = e.touches[0];
      const newX = touch.clientX - dragOffset.x;
      const maxX = window.innerWidth - mascotWidth - 20;
      setPosition({
        x: Math.max(20, Math.min(newX, maxX)),
      });
      e.preventDefault();
    };

    const handleTouchEnd = () => {
      setIsDragging(false);
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
    document.addEventListener("touchmove", handleTouchMove, { passive: false });
    document.addEventListener("touchend", handleTouchEnd);
    document.addEventListener("touchcancel", handleTouchEnd);

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
      document.removeEventListener("touchmove", handleTouchMove);
      document.removeEventListener("touchend", handleTouchEnd);
      document.removeEventListener("touchcancel", handleTouchEnd);
    };
  }, [isDragging, dragOffset, mascotWidth]);

  const calculateXByState = (windowWidth: number) => {
    const safeMargin = 20;

    switch (mascotPosition) {
      case "LEFT":
        return safeMargin;
      case "MIDDLE":
        return (windowWidth - mascotWidth) / 2;
      case "RIGHT":
      default:
        return windowWidth - positionXOffset;
    }
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    if (
      !dragHandleRef.current ||
      !dragHandleRef.current.contains(e.target as Node)
    ) {
      return;
    }

    e.preventDefault();

    const rect = mascotRef.current!.getBoundingClientRect();
    const offsetX = e.clientX - rect.left;
    const offsetY = e.clientY - rect.top;

    setDragOffset({ x: offsetX, y: offsetY });
    setIsDragging(true);
    hasBeenDraggedRef.current = true;
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    if (
      !dragHandleRef.current ||
      !dragHandleRef.current.contains(e.target as Node)
    ) {
      return;
    }

    const touch = e.touches[0];
    const rect = mascotRef.current!.getBoundingClientRect();
    const offsetX = touch.clientX - rect.left;
    const offsetY = touch.clientY - rect.top;

    setDragOffset({ x: offsetX, y: offsetY });
    setIsDragging(true);
    hasBeenDraggedRef.current = true;
  };

  const handleDragAreaHover = () => {
    setCanDragable(true);
    setShowDragIndicator(true);
  };

  const handleDragAreaLeave = () => {
    setCanDragable(false);
    setShowDragIndicator(false);
  };

  return (
    <>
      <style>{draggableMascotStyles}</style>
      <div
        ref={mascotRef}
        className={`${showMascot ? "" : "qav-hidden"} qav-root`}
        style={{
          left: `${position.x}px`,
          bottom: `${positionYOffset - mascotHeight}px`,
          width: mascotWidth,
          height: mascotHeight,
          willChange: "transform",
          transform: `translate3d(0, 0, 0)`,
        }}
      >
        <div
          ref={dragHandleRef}
          className="qav-drag-handle"
          style={{
            height: dragHeight,
            zIndex: 10,
            touchAction: "none",
          }}
          onMouseDown={handleMouseDown}
          onTouchStart={handleTouchStart}
          onMouseEnter={handleDragAreaHover}
          onMouseLeave={handleDragAreaLeave}
        />

        <div
          className={`qav-indicator ${
            showDragIndicator || canDragable || isDragging
              ? "qav-indicator-visible"
              : "qav-indicator-hidden"
          }`}
          style={{ zIndex: 15 }}
        >
          <div className="qav-indicator-inner">
            <div
              className="qav-indicator-label-wrap"
              style={{ right: -mascotWidth / 2 + 40 }}
            >
              <span className="qav-indicator-label">{i18n.t("drag")}</span>
            </div>
          </div>
        </div>

        {showDragIndicator && (
          <div
            className="qav-pulse-overlay"
            style={{
            height: dragHeight,
            background:
              "linear-gradient(to bottom, rgba(59, 130, 246, 0.1), transparent)",
            borderRadius: "8px 8px 0 0",
            zIndex: 5,
          }}
        />
      )}

      <Mascot
        userInformation={userInformation}
        userProfile={userProfile}
        userProfileSeason={userProfileSeason}
        curriculumType={userProfileGrade?.curriculumType}
        inventoryItemDTOList={mascotInventoryItemDTOList}
        mode={mascotMode}
        showText={showMascotText}
        setShowText={setShowMascotText}
        text={mascotText}
        subText={mascotSubText}
        primaryButtonText={mascotPrimaryButtonText}
        primaryButtonOnClick={mascotPrimaryButtonOnClick}
        chatActivityId={mascotChatActivityId}
        chatActivityTemplateId={mascotChatActivityTemplateId}
        chatActivityTemplateType={mascotChatActivityTemplateType}
        chatSize={mascotChatSize}
        chatWelcomeText={mascotChatWelcomeText}
        chatList={mascotChatList}
        chatLoading={mascotChatLoading}
        sendMessageOnClick={mascotSendMessageOnClick}
        chatCanConvertAudio={mascotChatCanConvertAudio}
        chatVoiceItemOwned={mascotHasVoiceItem}
        chatCanDraw={mascotChatCanDraw}
        chatVoiceType={mascotChatVoiceType}
        chatActType={mascotChatActType}
        chatPosition={
          mascotPosition === "LEFT"
            ? "RIGHT"
            : mascotPosition === "RIGHT"
              ? "LEFT"
              : "LEFT"
        }
        chatErrorText={mascotChatErrorText}
        chatContinueText={mascotChatContinueText}
        chatContinueOnClick={mascotChatContinueOnClick}
        chatActionList={mascotChatActionList}
      />
      </div>
    </>
  );
};

export default DraggableMascot;
